import { useEffect, useRef, useState } from 'react'
import {
  Search,
  Compass,
  PenTool,
  Rocket,
  BarChart3,
  ArrowRight,
  CheckCircle
} from 'lucide-react'

interface ProcessStep {
  id: number
  icon: React.ReactNode
  title: string
  description: string
  details: string[]
}

const processSteps: ProcessStep[] = [
  {
    id: 1,
    icon: <Search size={28} aria-hidden="true" />,
    title: 'Discovery',
    description: 'We dive deep into understanding your challenges, goals, and learner needs.',
    details: [
      'Stakeholder interviews',
      'Current state assessment',
      'Learner persona development',
      'Gap analysis',
    ],
  },
  {
    id: 2,
    icon: <Compass size={28} aria-hidden="true" />,
    title: 'Strategy',
    description: 'We design a roadmap aligned with your institutional goals and learner outcomes.',
    details: [
      'Technology recommendations',
      'Implementation timeline',
      'Success metrics definition',
      'Risk mitigation planning',
    ],
  },
  {
    id: 3,
    icon: <PenTool size={28} aria-hidden="true" />,
    title: 'Design',
    description: 'We craft engaging learning experiences using proven instructional design principles.',
    details: [
      'Learning experience design',
      'Content architecture',
      'Interaction patterns',
      'Accessibility integration',
    ],
  },
  {
    id: 4,
    icon: <Rocket size={28} aria-hidden="true" />,
    title: 'Implementation',
    description: 'We guide you through seamless deployment with hands-on support.',
    details: [
      'Phased rollout',
      'Change management',
      'Staff training',
      'Technical integration',
    ],
  },
  {
    id: 5,
    icon: <BarChart3 size={28} aria-hidden="true" />,
    title: 'Optimization',
    description: 'We continuously measure, analyze, and improve for lasting impact.',
    details: [
      'Analytics review',
      'Learner feedback analysis',
      'Iterative improvements',
      'ROI reporting',
    ],
  },
]

export const Process: React.FC = () => {
  const [activeStep, setActiveStep] = useState<number | null>(null)
  const [visibleSteps, setVisibleSteps] = useState<Set<number>>(new Set())
  const sectionRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const stepId = parseInt(entry.target.getAttribute('data-step') || '0')
            if (stepId) {
              setVisibleSteps((prev) => new Set([...prev, stepId]))
            }
          }
        })
      },
      {
        threshold: 0.3,
        rootMargin: '0px 0px -100px 0px',
      }
    )

    const steps = sectionRef.current?.querySelectorAll('[data-step]')
    steps?.forEach((step) => observer.observe(step))

    return () => observer.disconnect()
  }, [])

  return (
    <section
      id="process"
      ref={sectionRef}
      className="section-alt scroll-mt-20 overflow-hidden"
      aria-labelledby="process-heading"
    >
      {/* Decorative background */}
      <div className="absolute inset-0 bg-grid-pattern opacity-20" aria-hidden="true" />
      <div className="absolute top-10 right-10 w-64 h-64 bg-brand-blue/5 rounded-full blur-3xl" aria-hidden="true" />
      <div className="absolute bottom-10 left-10 w-80 h-80 bg-brand-amber/5 rounded-full blur-3xl" aria-hidden="true" />

      {/* Animated decorative shapes */}
      <div className="absolute top-20 left-[10%] w-20 h-20 border-2 border-brand-blue/10 rounded-full animate-pulse" aria-hidden="true" />
      <div className="absolute bottom-40 right-[15%] w-12 h-12 bg-brand-amber/10 rounded-lg rotate-45 animate-float" aria-hidden="true" />

      <div className="container-wide relative">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-1.5 bg-brand-amber/10 text-brand-amber text-sm font-semibold rounded-full mb-4">
            Our Process
          </span>
          <h2
            id="process-heading"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6"
          >
            How We Work
          </h2>
          <p className="text-lg text-slate-600">
            A proven methodology that transforms your learning vision into reality.
            We partner with you at every step to ensure lasting success.
          </p>
        </div>

        {/* Process Steps - Interactive Timeline */}
        <div className="relative">
          {/* Connection Line */}
          <div
            className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-brand-blue via-brand-amber to-brand-blue transform -translate-y-1/2"
            aria-hidden="true"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-4">
            {processSteps.map((step, index) => (
              <div
                key={step.id}
                data-step={step.id}
                className={`relative transition-all duration-700 ${
                  visibleSteps.has(step.id)
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-12'
                }`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                {/* Step Card */}
                <div
                  className={`relative bg-white rounded-2xl p-6 border-2 transition-all duration-300 cursor-pointer ${
                    activeStep === step.id
                      ? 'border-brand-blue shadow-xl shadow-brand-blue/20 scale-105'
                      : 'border-slate-200 hover:border-brand-blue/50 hover:shadow-lg'
                  }`}
                  onClick={() => setActiveStep(activeStep === step.id ? null : step.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setActiveStep(activeStep === step.id ? null : step.id)
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-expanded={activeStep === step.id}
                  aria-label={`Step ${step.id}: ${step.title}. Click to ${activeStep === step.id ? 'collapse' : 'expand'} details.`}
                >
                  {/* Step Number Badge */}
                  <div
                    className={`absolute -top-3 -left-3 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                      activeStep === step.id
                        ? 'bg-brand-blue text-white'
                        : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    {step.id}
                  </div>

                  {/* Icon */}
                  <div
                    className={`w-14 h-14 rounded-xl flex items-center justify-center mb-4 transition-colors ${
                      activeStep === step.id
                        ? 'bg-brand-blue text-white'
                        : 'bg-gradient-to-br from-brand-blue/10 to-brand-amber/10 text-brand-blue'
                    }`}
                  >
                    {step.icon}
                  </div>

                  {/* Content */}
                  <h3 className="text-lg font-bold text-slate-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm text-slate-600 mb-4">{step.description}</p>

                  {/* Expanded Details */}
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      activeStep === step.id ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <ul className="space-y-2 pt-4 border-t border-slate-100">
                      {step.details.map((detail) => (
                        <li
                          key={detail}
                          className="flex items-center gap-2 text-sm text-slate-600"
                        >
                          <CheckCircle size={14} className="text-brand-amber flex-shrink-0" aria-hidden="true" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Expand Indicator */}
                  <div className="flex items-center justify-center mt-4">
                    <span
                      className={`text-xs font-medium transition-colors ${
                        activeStep === step.id ? 'text-brand-blue' : 'text-slate-400'
                      }`}
                    >
                      {activeStep === step.id ? 'Click to collapse' : 'Click to expand'}
                    </span>
                  </div>
                </div>

                {/* Arrow connector (hidden on last item and mobile) */}
                {index < processSteps.length - 1 && (
                  <div
                    className="hidden lg:flex absolute top-1/2 -right-4 transform -translate-y-1/2 z-10"
                    aria-hidden="true"
                  >
                    <ArrowRight size={20} className="text-brand-amber" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <p className="text-slate-600 mb-4">
            Ready to start your transformation journey?
          </p>
          <a
            href="#contact"
            className="btn-primary inline-flex"
            onClick={(e) => {
              e.preventDefault()
              document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })
            }}
          >
            Start a Conversation
            <ArrowRight size={18} aria-hidden="true" />
          </a>
        </div>
      </div>
    </section>
  )
}
